import { itineraryPrePaymentApi } from '@/src/api/requests/itinerary'
import itineraryApi, { useUpdateItineraryMutation } from '@/src/api/rtkQuery/itineraryApi'
import { ErrorModalWrapperProps } from '@/src/components/common/itinerary/errorModal'
import { FLIGHT_NOT_AVAILABLE, PRICE_CHANGED } from '@/src/constants/itineraryStatus'
import useClevertapForItinerary from '@/src/hooks/analytics/clevertap/itinerary'
import useTravelersGtmEvent from '@/src/hooks/analytics/gtm/travelers'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'
import { useAppDispatch } from '@/src/redux/store'
import { DeviceType } from '@/src/types/deviceType'
import { Customer, TaxDetails, Traveler } from '@/src/types/itinerary'
import { getGtmPayloadForPayment } from '@/src/utils/analytics/gtm/getGtmPayloadForPayment'
import { isEmpty } from '@/src/utils/generalUtils/isEmpty'
import { getTravelerDetailsPayload } from '@/src/utils/itinerary/updatePayloadHelper'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'
import useGetItineraryId from '../../useGetItineraryId'
import { useLazyUserLogoutQuery } from '@/src/api/rtkQuery/userApi'
import { clearAuthUser, refreshAuthUser } from '@/src/redux/slices/authSlice'
import { deleteCookie } from '@/src/utils/browser'
import { useRouter } from 'next/navigation'

export const useUpdateItineraryAndPrepayment = (props: {
    travelers: Traveler[]
    itineraryId: string
    contactDetails: Customer
    isDateOfBirthRequired: boolean
    isNationalityRequiredToRenderInUserPersonalDetailsSection: boolean
    isBirthCountryRequired: boolean
    taxDetails: TaxDetails
    deviceType: DeviceType
}) => {
    const itineraryId = useGetItineraryId()
    const dispatch = useAppDispatch()
    const prepaymentTryCountRef = useRef(0)
    const t = useTranslations('common')
    const { showError } = useThemedToast()
    const [
        updateItinerary,
        { isLoading: isUpdateItineraryLoading, isError: isUpdateItineraryError, isSuccess: isUpdateItinerarySuccess }
    ] = useUpdateItineraryMutation()
    const { triggerItineraryEvent } = useClevertapForItinerary()
    const { pushEventToDataLayer } = useTravelersGtmEvent()

    const [{ isPrePaymentError, isPrePaymentLoading, isPrePaymentSuccess }, setPrePayment] = useState({
        isPrePaymentLoading: false,
        isPrePaymentError: false,
        isPrePaymentSuccess: false
    })
    const [prePaymentErrorType, setPrePaymentErrorType] = useState<{
        error: ErrorModalWrapperProps['errorType']
        message?: ErrorModalWrapperProps['message']
    } | null>(null)

    const [triggerLogout] = useLazyUserLogoutQuery();
    const router = useRouter();

    const [showCouponExpiry, setShowCouponExpiry] = useState<boolean>(false)

    const handlePrePayment = async () => {
        try {
            const prePaymentPayload = {
                itineraryId: props.itineraryId,
                deviceType: props.deviceType
            }
            setPrePayment({
                isPrePaymentLoading: true,
                isPrePaymentError: false,
                isPrePaymentSuccess: false
            })
            const primaryTraveler = props.travelers[0]
            const gtmPayload = getGtmPayloadForPayment({
                primaryTraveler,
                contactDetails: props.contactDetails
            })
            let prepaymentResponse = (await itineraryPrePaymentApi(prePaymentPayload)) as Record<string, any>

            if (!isEmpty(prepaymentResponse)) {
                const {
                    data : {
                            data: {
                            prepaymentStatus: {
                                itineraryStatus: prepaymentItineraryStatus = '',
                                searchCriteria: prepaymentSearchCriteria
                            } = {}
                        } = {},
                        status : responseStatus,
                    } = {}
                } = prepaymentResponse || {}

                if (responseStatus === 401) {
                    await triggerLogout()?.unwrap()
                    dispatch(clearAuthUser())
                    const domain = window.location.hostname
                    deleteCookie('usermisc', domain)
                    deleteCookie('userid', domain)
                    deleteCookie('userData', domain)
                    dispatch(refreshAuthUser())
                    router.push('/')
                    return
                }


                if (prepaymentItineraryStatus === 'HOLD') {
                    if (prepaymentTryCountRef.current < 1) {
                        prepaymentTryCountRef.current = prepaymentTryCountRef.current + 1
                    } else {
                        // setPrePaymentStatusObj({
                        //     variant: 'HOLD',
                        //     searchCriteria: prepaymentSearchCriteria
                        // })
                    }
                }

                let {
                    data : {
                        data: prepaymentData,
                        payment_url : responseURL = "",
                        status: prepaymentStatus,
                    } = {},
                } = prepaymentResponse || {}
                if (prepaymentStatus === 200 || prepaymentStatus === 302) {
                    if (typeof prepaymentData === 'string' && responseURL) {
                        triggerItineraryEvent('Air_UI_Action', {
                            action_type: 'BUTTON',
                            action_name: 'prepayment_api_success',
                            payment_url: responseURL
                        })

                        // localStorage.removeItem(itineraryData.itineraryId)
                        // localStorage.removeItem(previousItinDataKey)
                        // const queryString = window.location.search
                        // const ind = getURLSearchParam('index')
                        // const departind = getURLSearchParam('departInd')
                        // const returnind = getURLSearchParam('returnInd')
                        // const searchQuery = ind ? `index=${ind}` : `departInd=${departind}&returnInd=${returnind}`
                        pushEventToDataLayer(gtmPayload)
                        setTimeout(() => {
                            window.location.href = `${responseURL}`
                        }, 0)
                    } else if (prepaymentData?.payUrlResponse?.payment_url) {
                        triggerItineraryEvent('Air_UI_Action', {
                            action_type: 'BUTTON',
                            action_name: 'prepayment_api_success',
                            payment_url: prepaymentData?.payUrlResponse?.payment_url
                        })
                        pushEventToDataLayer(gtmPayload)
                        setTimeout(() => {
                            window.location.href = prepaymentData?.payUrlResponse?.payment_url
                        }, 0)
                    } else {
                        setPrePayment({
                            isPrePaymentLoading: false,
                            isPrePaymentError: false,
                            isPrePaymentSuccess: false
                        })
                    }
                } else {
                    // setPaymentButtonLoading(false)
                    // localStorage.setItem(previousItinDataKey, JSON.stringify(defaultTravellerData.data))
                    const {
                        prepaymentStatus: {
                            itineraryStatus = '',
                            // newAirlinePrice,
                            // newTotalPrice,
                            // searchCriteria: prepaymentSearchCriteria,
                            updatedFare = {
                                oldPrice: 0,
                                totalPrice: 0
                            }
                        } = {}
                    } = prepaymentData || {}

                    // const getLocalStorageResponse = JSON.parse(localStorage.getItem(itineraryData.itineraryId))

                    // Response is 206 when coupon expires on prepayment
                    if (prepaymentStatus == 206) {
                        // setShowCouponExpiry(
                        //     prepaymentData?.couponDetails?.errorCouponData?.message ||
                        //         'The coupon you applied for is no longer available. You may select a new coupon or continue with your payment.'
                        // )
                        setPrePayment({
                            isPrePaymentLoading: false,
                            isPrePaymentError: false,
                            isPrePaymentSuccess: false
                        })
                        setShowCouponExpiry(true)
                        return
                    }
                    if (itineraryStatus === PRICE_CHANGED) {
                        // setItineraryUpdatedFare(updatedFare)
                        // const { fareDetails = {} } = priceSummary || {}
                        // const previousPrice =
                        //     fareDetails.basePrice && fareDetails.taxes && fareDetails.basePrice + fareDetails.taxes
                        // setPrePaymentStatusObj({
                        //     variant: 'PRICE_CHANGED',
                        //     newPrice: newAirlinePrice,
                        //     oldPrice: previousPrice,
                        //     searchCriteria: prepaymentSearchCriteria
                        // })
                        triggerItineraryEvent('Air_UI_Action', {
                            action_type: 'BUTTON',
                            action_name: 'prepayment_api_price_changed'
                        })

                        dispatch(
                            itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                                draft.metaData.priceChangePopupData = {
                                    ...draft.metaData.priceChangePopupData,
                                    callPrePaymentOnAcceptPriceChange: true,
                                    isPriceChange: true,
                                    newPrice: updatedFare.totalPrice,
                                    oldPrice: updatedFare.oldPrice,
                                    priceChangePopupShown: false
                                }
                            })
                        )
                        // setPrePaymentErrorType({
                        //     error: PRICE_CHANGED,
                        //     message: {
                        //         primary: t('priceChanged'),
                        //         secondary: t('updatedPrice', { price: newTotalPrice })
                        //     }
                        // })
                        setPrePayment({
                            isPrePaymentLoading: false,
                            isPrePaymentError: false,
                            isPrePaymentSuccess: false
                        })
                    } else if (itineraryStatus === FLIGHT_NOT_AVAILABLE) {
                        triggerItineraryEvent('Air_Failures', {
                            failure_reason: 'Flight not available',
                            failure_type: 'prepayment_api_flight_not_available'
                        })
                        setPrePaymentErrorType({
                            error: FLIGHT_NOT_AVAILABLE
                        })
                        setPrePayment({
                            isPrePaymentLoading: false,
                            isPrePaymentError: false,
                            isPrePaymentSuccess: false
                        })
                    } else {
                        setPrePayment({
                            isPrePaymentLoading: false,
                            isPrePaymentError: false,
                            isPrePaymentSuccess: false
                        })
                    }
                }
            } else {
                setPrePayment({
                    isPrePaymentLoading: false,
                    isPrePaymentError: false,
                    isPrePaymentSuccess: false
                })
            }
        } catch (e) {
            // console.log(e)
            triggerItineraryEvent('Air_Failures', {
                failure_reason: 'An unexpected error occurred. Please try again.',
                failure_type: 'prepayment_api_error'
            })
            setPrePayment({
                isPrePaymentLoading: false,
                isPrePaymentError: true,
                isPrePaymentSuccess: false
            })
        }
    }

    const handleUpdateItineraryAndPrepayment = async () => {
        const payload = getTravelerDetailsPayload(props)
        try {
            triggerItineraryEvent('Air_UI_Action', {
                action_type: 'BUTTON',
                action_name: 'travellers_make_payment_clicked'
            })
            const response = await updateItinerary({ payload })
            if (response.data?.error || !response.data.successMetrics) {
                triggerItineraryEvent('Air_Failures', {
                    failure_reason: response.data?.error?.message || t('updateApiError'),
                    failure_type: 'travellers_update_itinerary_error'
                })

                showError(t('updateApiError'), {
                    id: 'update-itinerary-error'
                })

                return
            }

            await handlePrePayment()
        } catch (error) {
            triggerItineraryEvent('Air_Failures', {
                failure_reason: 'An unexpected error occurred. Please try again.',
                failure_type: 'travellers_update_itinerary_error'
            })
            showError(t('updateApiError'), {
                id: 'update-itinerary-unexpected-error'
            })
        }
    }

    const onPageShow = () => {
        if (isPrePaymentLoading) {
            setPrePayment(prev => ({
                ...prev,
                isPrePaymentLoading: false
            }))
        }
    }

    useEffect(() => {
        window.addEventListener('pageshow', onPageShow)
        onPageShow()
        return () => {
            window.removeEventListener('pageshow', onPageShow)
        }
    }, [])

    return {
        isUpdateItineraryLoading,
        isUpdateItineraryError,
        isUpdateItinerarySuccess,
        isPrePaymentLoading,
        isPrePaymentError,
        isPrePaymentSuccess,
        showCouponExpiry,
        setShowCouponExpiry,
        handleUpdateItineraryAndPrepayment,
        handlePrePayment,
        prePaymentErrorType
    }
}
